import type { Ref } from 'vue'
import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import { useHandwritingProjectService } from '~~/layers/base/services/handwritingProject.service'
import { useUserImageService } from '~~/layers/base/services/userImage.service'
import { debounce } from '~~/layers/base/utils/debounce'
import {
    buildHandwrittenEnvelopeFacePreviewEntry,
    fingerprintHandwrittenPreviewDataUrl,
    handwrittenPreviewDataUrlToBlob,
} from '~~/layers/base/utils/handwrittenHeroPreviewUpload'
import { mergeDenormalizedPreviewIntoArray } from '~~/layers/base/utils/handwritingProjectDenormalizedPreview'
import { createRequestStartThrottle } from '~~/layers/base/utils/requestStartThrottle'

type FacePreviewPayload = {
    dataUrl: string | null
    meta: Record<string, unknown>
}

/**
 * Handwritten send-flow **envelope face** preview upload (composed recipient/return Fabric bitmap).
 *
 * Mirrors {@link useHandwrittenHeroPreviewUpload}: separate debounce/throttle/session `user_image` row +
 * PATCH denormalized `preview_image` on the primary `handwritten_note` (`envelope_face` slot).
 */
export function useHandwrittenEnvelopeFacePreviewUpload(params: {
    isResettingForm: Ref<boolean>
    autosaveEnabled: Ref<boolean>
    selectedHandwritingProjectId: Ref<string | null>
    projectDenormalizedPreviews: Ref<StationeryPreviewImage[]>
}) {
    const {
        isResettingForm,
        autosaveEnabled,
        selectedHandwritingProjectId,
        projectDenormalizedPreviews,
    } = params

    /**
     * Latest envelope-face preview payload from personalize (composed Fabric `data:` URL + meta).
     * Cleared when the payload is not a data URL.
     */
    const envelopeFacePreviewLatest = ref<{
        dataUrl: string
        meta: Record<string, unknown>
    } | null>(null)

    /**
     * Session-only `user_image.id` for this pipeline’s envelope-face row (POST then PATCH).
     */
    const currentPreviewUserImageId = ref<string | null>(null)

    /** Fingerprint of the last successfully uploaded preview (in-memory dedupe). */
    const lastUploadedPreviewFingerprint = ref<string | null>(null)

    /**
     * Latest upload reference for this session: prefer `cloudflare_image_id`,
     * otherwise the source data URL.
     */
    const latestPreviewUploadReference = ref<string | null>(null)
    /**
     * Serializes `user_image` writes (POST/PATCH) and the downstream
     * `handwriting_project.preview_image` PATCH. Surfaced to the store as
     * `userImageRequestInFlight` for UI indicators.
     */
    const userImageWriteInFlight = ref(false)
    /** When true, one follow-up upload runs after `userImageWriteInFlight` clears. */
    const rerunAfterInFlightWrite = ref(false)

    const MIN_PREVIEW_UPLOAD_GAP_MS = 2000
    const throttle = createRequestStartThrottle(MIN_PREVIEW_UPLOAD_GAP_MS)

    const {
        debouncedFn: debouncedFacePreviewUpload,
        cancel: cancelEnvelopeFacePreviewUploadDebounce,
    } = debounce(
        () => {
            void tryRunEnvelopeFacePreviewUpload()
        },
        500,
        'envelopeFacePreviewUpload'
    )

    /**
     * Receives composed envelope-face preview from the personalize UI. Debounced before any network work.
     *
     * @param payload - Fabric snapshot (`data:` URL or `null`) plus stationery meta for `preview_image`.
     */
    function onEnvelopeFacePreviewPayload(payload: FacePreviewPayload) {
        if (!payload.dataUrl?.startsWith('data:')) {
            envelopeFacePreviewLatest.value = null
            return
        }
        envelopeFacePreviewLatest.value = { dataUrl: payload.dataUrl, meta: payload.meta }
        debouncedFacePreviewUpload()
    }

    /**
     * Guards (reset/autosave/project), fingerprint dedupe, in-flight queueing, request-start throttle,
     * then runs {@link executeEnvelopeFacePreviewUpload}.
     */
    function tryRunEnvelopeFacePreviewUpload() {
        if (isResettingForm.value) {
            return
        }
        if (!autosaveEnabled.value || !selectedHandwritingProjectId.value) {
            return
        }
        const snapshot = envelopeFacePreviewLatest.value
        if (!snapshot) {
            return
        }

        const fingerprint = fingerprintHandwrittenPreviewDataUrl(snapshot.dataUrl)
        if (fingerprint === lastUploadedPreviewFingerprint.value) {
            return
        }
        if (userImageWriteInFlight.value) {
            rerunAfterInFlightWrite.value = true
            return
        }

        if (
            !throttle.canStartNowOrSchedule(() => {
                void tryRunEnvelopeFacePreviewUpload()
            })
        ) {
            return
        }

        void executeEnvelopeFacePreviewUpload(snapshot, fingerprint)
    }

    /**
     * Converts the snapshot to a blob, upserts `user_image`, merges `envelope_face` into the denormalized
     * `preview_image` array, and PATCHes the handwriting project. Updates fingerprints on success;
     * clears `userImageWriteInFlight` in `finally` and may rerun once if newer input arrived mid-flight.
     *
     * @param snapshot - Preview bitmap + meta captured before async gaps.
     * @param fingerprint - Dedupe key from {@link fingerprintHandwrittenPreviewDataUrl}.
     */
    async function executeEnvelopeFacePreviewUpload(
        snapshot: { dataUrl: string; meta: Record<string, unknown> },
        fingerprint: string
    ) {
        const projectId = selectedHandwritingProjectId.value
        if (!projectId) {
            return
        }

        // Matches tryRun throttle gate: counts this attempt toward MIN_PREVIEW_UPLOAD_GAP_MS.
        throttle.markStartedNow()

        // Multipart APIs want a Blob; empty means corrupt/unusable canvas export.
        const blob = await handwrittenPreviewDataUrlToBlob(snapshot.dataUrl)
        if (!blob || blob.size === 0) {
            return
        }

        const { doCreateUserImage, doPatchUserImageWithImage } = useUserImageService()
        const { doPatchHandwritingProject } = useHandwritingProjectService()

        userImageWriteInFlight.value = true
        try {
            let cloudflareId: string

            // One session-scoped user_image row per project: POST first, then PATCH the same id on edits.
            if (!currentPreviewUserImageId.value) {
                const { data, error } = await doCreateUserImage({
                    image: blob,
                    handwriting_project_id: projectId,
                })
                if (error.value || !data.value?.id || !data.value.cloudflare_image_id) {
                    // eslint-disable-next-line no-console
                    console.error('Envelope face preview upload (create) failed:', error.value)
                    return
                }
                currentPreviewUserImageId.value = data.value.id
                cloudflareId = data.value.cloudflare_image_id
            } else {
                const { data, error } = await doPatchUserImageWithImage(
                    currentPreviewUserImageId.value,
                    blob
                )
                if (error.value || !data.value?.cloudflare_image_id) {
                    // eslint-disable-next-line no-console
                    console.error('Envelope face preview upload (patch) failed:', error.value)
                    return
                }
                cloudflareId = data.value.cloudflare_image_id
            }

            // Denormalized preview array: upsert envelope_face slot, keep other slots untouched.
            const previewEntry = buildHandwrittenEnvelopeFacePreviewEntry(
                cloudflareId,
                snapshot.meta
            )
            const merged = mergeDenormalizedPreviewIntoArray(
                projectDenormalizedPreviews.value,
                previewEntry
            )
            projectDenormalizedPreviews.value = merged

            // Persist merged array so status/success routes and other clients see the Cloudflare-backed URL.
            const { error: patchErr } = await doPatchHandwritingProject(projectId, {
                preview_image: merged,
            })
            if (patchErr.value) {
                // eslint-disable-next-line no-console
                console.error('Envelope face preview_image PATCH failed:', patchErr.value)
                return
            }

            // Success only: fingerprint prevents redundant uploads; cf id helps callers/debugging.
            lastUploadedPreviewFingerprint.value = fingerprint
            latestPreviewUploadReference.value = cloudflareId || snapshot.dataUrl
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Envelope face preview upload exception:', e)
        } finally {
            // Always drop the lock so personalize UI can reflect idle state.
            userImageWriteInFlight.value = false
            // New snapshot may have landed during POST/PATCH; single queued rerun picks up latest.
            if (rerunAfterInFlightWrite.value) {
                rerunAfterInFlightWrite.value = false
                void tryRunEnvelopeFacePreviewUpload()
            }
        }
    }

    /** Cancel pending debounce callback and throttle-delay callback. */
    function cancelPendingEnvelopeFacePreviewUploadWork() {
        cancelEnvelopeFacePreviewUploadDebounce()
        throttle.clearWait()
    }

    /**
     * Clear envelope-face upload state and throttle timer. Call after
     * {@link cancelPendingEnvelopeFacePreviewUploadWork} so pending debounced work is already cancelled.
     */
    function resetEnvelopeFacePreviewUploadSession() {
        throttle.reset()
        envelopeFacePreviewLatest.value = null
        currentPreviewUserImageId.value = null
        lastUploadedPreviewFingerprint.value = null
        latestPreviewUploadReference.value = null
        userImageWriteInFlight.value = false
        rerunAfterInFlightWrite.value = false
    }

    return {
        onEnvelopeFacePreviewPayload,
        cancelPendingEnvelopeFacePreviewUploadWork,
        resetEnvelopeFacePreviewUploadSession,
        /** Latest composed envelope-face Fabric `data:` URL + meta (session-only; used on Send store cache). */
        envelopeFacePreviewLatest,
        /**
         * True while the user_image POST/PATCH **and** the downstream
         * `handwriting_project.preview_image` PATCH are in flight for the envelope-face pipeline.
         */
        userImageRequestInFlight: userImageWriteInFlight,
    }
}
